import classNames from 'classnames';
import React from 'react';
import acceptIcon from '../icons/non-prefilled/12x12/accept.svg';
import archiveIcon from '../icons/non-prefilled/12x12/archive.svg';
import commentIcon from '../icons/non-prefilled/12x12/comment.svg';
import editIcon from '../icons/non-prefilled/12x12/edit.svg';
import mentionIcon from '../icons/non-prefilled/12x12/mention.svg';
import publishIcon from '../icons/non-prefilled/12x12/publish.svg';
import replyIcon from '../icons/non-prefilled/12x12/reply.svg';
import resolveIcon from '../icons/non-prefilled/12x12/resolve.svg';
import thankIcon from '../icons/non-prefilled/16x16/thanks.svg';
import shareIcon from '../icons/non-prefilled/12x12/share.svg';
import verifyIcon from '../icons/non-prefilled/16x16/verified.svg';
import userPath from '../utils/url/userPath';
import Avatar from './Avatar';
import styles from './AvatarWithAction.module.scss';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import Link from '../utils/router/Link';

const AVATAR_WITH_ACTION_USER = gql(`
  fragment AvatarWithAction_user on User {
    id
    ...Avatar_user
  }
`);

interface ActionProp {
  color: string;
  icon?: string;
  size: SVGIconSize;
}

export const propsByAction: { [key: string]: ActionProp } = {
  COMMENT_CREATE: { color: 'comment', icon: commentIcon, size: 'small' },
  COMMENT_MENTION: { color: 'comment', icon: mentionIcon, size: 'small' },
  COMMENT_REPLY: { color: 'comment', icon: replyIcon, size: 'small' },
  COMMENT_RESOLVE: { color: 'comment', icon: resolveIcon, size: 'small' },
  INVITE_ACCEPT: { color: 'success', icon: acceptIcon, size: 'small' },
  POST_ARCHIVE: { color: 'post', icon: archiveIcon, size: 'small' },
  POST_EDIT: { color: 'post', icon: editIcon, size: 'small' },
  POST_PUBLISH: { color: 'post', icon: publishIcon, size: 'small' },
  POST_SHARE: { color: 'post', icon: shareIcon, size: 'small' },
  POST_THANK: { color: 'post', icon: thankIcon, size: 'small' },
  REACTION_CREATE: { color: 'comment', size: 'small' },
  VERIFICATION_EXPIRED: { color: 'muted', icon: verifyIcon, size: 'regular' },
  VERIFICATION_REMINDER: {
    color: 'warning',
    icon: verifyIcon,
    size: 'regular',
  },
  VERIFICATION_REQUEST: { color: 'success', icon: verifyIcon, size: 'regular' },
};

type AvatarWithActionProps = React.ComponentProps<typeof Avatar> & {
  action: keyof typeof propsByAction;
  emoji?: string;
  link?: boolean;
  user: DocumentType<typeof AVATAR_WITH_ACTION_USER>;
};

const AvatarWithAction = ({
  action,
  emoji,
  link = false,
  ...avatarProps
}: AvatarWithActionProps) => {
  const props = propsByAction[action];
  if (!props) return null;

  const { size, icon, color } = props;

  const inner = (
    <div className={styles.avatarContainer} role="img">
      <Avatar {...avatarProps} />
      <div className={styles.iconContainer}>
        <div
          className={classNames(styles.icon, styles[color])}
          role="presentation"
        >
          <SVGIcon size={size}>{icon || emoji}</SVGIcon>
        </div>
      </div>
    </div>
  );

  return link ? <Link to={userPath(avatarProps.user)}>{inner}</Link> : inner;
};

export default AvatarWithAction;
